import {
  Alert,
  Button,
  CloseButton,
  Flex,
  Grid,
  Heading,
  IconImportLine,
  IconSisSyncedLine,
  Modal,
  Spinner,
  Text,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useRef, useState } from "react";
import { LearningOutcomesService } from "../../../services/LearningOutcomesService";
import { MasteryLevelContext } from "../../../utils/Context";
import RaterGridAdd from "../../Assessment/Plan/RaterGrid/RaterGridAdd";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import RatingScaleSelect from "../../Selectors/RatingScaleSelect";

const RubricMappingModal = ({
  open,
  setOpen,
  outcome,
  setOutcome,
  setRubricTrigger,
  selectedMapping,
}) => {
  const showNotification = useNotification();
  const { ratingScales } = useContext(MasteryLevelContext);
  const [selectedMap, setSelectedMap] = useState(selectedMapping ?? {});
  const [mapLoad, setMapLoad] = useState(false);
  const [rubricData, setRubricData] = useState({
    id: "",
    map_id: "",
    levels: [],
    mastery_at: "",
    rating_scale_id: "",
  });
  const [enableSave, setEnableSave] = useState(false);
  const [defaultLoader, setDefaultLoader] = useState(false);
  const raterGridRef = useRef();

  useEffect(() => {
    let interaction = true;
    if (Object.keys(selectedMap).length !== 0) {
      rubricData.levels.forEach((lvl) => {
        if (!lvl.description) {
          interaction = false;
        }
      });
    } else {
      interaction = false;
    }
    setEnableSave(interaction);
  }, [rubricData, selectedMap]);

  const mapSelect = (map) => {
    const mapping = ratingScales.find((mapping) => mapping.id === map);
    mapping.levels.forEach((level) => {
      level.label = level.points + " : " + level.description;
    });
    setSelectedMap(mapping);
  };

  const handleExit = () => {
    setRubricData({
      id: "",
      map_id: "",
      levels: [],
      mastery_at: "",
      rating_scale_id: "",
    });
    setSelectedMap(selectedMapping ?? {});
  };

  const handleSave = () => {
    setMapLoad(true);
    LearningOutcomesService.updateOutcomeRatingScaleMap(rubricData)
      .then(() => {
        showNotification(t("ratingScale.criteriaSuccess"));
        setMapLoad(false);
        setRubricTrigger(true);
      })
      .catch(() => {
        showNotification(t("ratingScale.criteriaError"), ERROR);
        setMapLoad(false);
      });
  };

  const handleCascadeClick = () => {
    if (raterGridRef.current) {
      raterGridRef.current.handleCascade();
    }
  };

  const handleSetDefault = async () => {
    try {
      setDefaultLoader(true);
      const newOutcome = { ...outcome };
      const newDefaultRatingScale = newOutcome.rating_scales.find(
        (s) => s.rating_to_mastery_level_map === selectedMap.id
      );
      await LearningOutcomesService.updateRatingScaleDefault(
        newOutcome.id,
        newDefaultRatingScale.ddl_id
      );
      const prevDefaultScale = newOutcome.rating_scales.find(
        (s) => s.is_default
      );
      if (prevDefaultScale) {
        prevDefaultScale.is_default = false;
      }
      newDefaultRatingScale.is_default = true;
      setOutcome(newOutcome);
      showNotification(t("ratingScale.setDefaultSuccess"));
    } catch {
      showNotification(t("ratingScale.setDefaultError"), ERROR);
    } finally {
      setDefaultLoader(false);
    }
  };

  const defaultButtonPermission = () => {
    const selectedScale = outcome.rating_scales.find(
      (s) => s.rating_to_mastery_level_map === selectedMap.id
    );
    return !defaultLoader && selectedScale && selectedScale.is_default === false
      ? "enabled"
      : "disabled";
  };

  return (
    <Modal
      open={open}
      size="large"
      label={t("analyze.rubricModal.outcomeRatings")}
      onExited={handleExit}
      onDismiss={() => setOpen(false)}
    >
      <Modal.Header>
        <Grid hAlign="space-between" vAlign="middle">
          <Grid.Row>
            <Grid.Col>
              <Heading level="h2" as="h1">
                {t("analyze.rubricModal.outcomeRatings")}
              </Heading>
            </Grid.Col>
            <Grid.Col width="auto">
              <CloseButton
                size="small"
                screenReaderLabel={t("global.close")}
                onClick={() => setOpen(false)}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </Modal.Header>
      <Modal.Body>
        <Flex direction="column" gap="small">
          <Flex.Item padding="0 xx-small">
            <Alert>
              {outcome.outcome_type === "CSLO"
                ? t("analyze.rubricModal.rubricDescriptionClo")
                : t("analyze.rubricModal.rubricDescriptionIloPlo")}
            </Alert>
          </Flex.Item>
          <Flex.Item padding="xx-small">
            <Text>
              <Text weight="bold">{t("ratingScale.lo")}: </Text>
              <Text>{outcome.name}</Text>
            </Text>
          </Flex.Item>
          <Flex.Item overflowY="hidden" overflowX="hidden" padding="xx-small">
            <Flex gap="small">
              <Flex.Item shouldGrow>
                <RatingScaleSelect
                  title={t("analyze.rubricModal.selectRatingScale")}
                  selectHandler={mapSelect}
                  size="small"
                  masteryMapping={selectedMap.id}
                  outcome={outcome}
                />
              </Flex.Item>
              <Flex.Item align="end">
                <Button
                  size="small"
                  renderIcon={
                    defaultLoader ? (
                      <Spinner
                        size="x-small"
                        renderTitle={t("global.pleaseWait")}
                      />
                    ) : (
                      IconSisSyncedLine
                    )
                  }
                  onClick={handleSetDefault}
                  interaction={defaultButtonPermission()}
                >
                  {t("ratingScale.setAsDefault")}
                </Button>
              </Flex.Item>
              <Flex.Item align="end">
                <Button
                  color="primary"
                  size="small"
                  renderIcon={IconImportLine}
                  onClick={handleCascadeClick}
                >
                  {t("analyze.rubricModal.inheritDescriptions")}
                </Button>
              </Flex.Item>
            </Flex>
          </Flex.Item>
          <Flex.Item padding="xx-small">
            <RaterGridAdd
              ref={raterGridRef}
              outcome={outcome}
              masteryMapping={selectedMap}
              adding={true}
              rubricData={rubricData}
              setRubricData={setRubricData}
            />
          </Flex.Item>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Button margin="0 small 0 0" onClick={() => setOpen(false)}>
          {t("global.close")}
        </Button>
        <Tooltip
          renderTip={t("ratingScale.selectScale")}
          color="primary-inverse"
          placement="bottom"
          on={enableSave ? [] : ["hover", "focus"]}
        >
          <Button
            color="primary"
            onClick={handleSave}
            interaction={enableSave && !mapLoad ? "enabled" : "disabled"}
          >
            {mapLoad ? (
              <Spinner size="x-small" renderTitle={t("global.pleaseWait")} />
            ) : (
              <Text>{t("global.save")}</Text>
            )}
          </Button>
        </Tooltip>
      </Modal.Footer>
    </Modal>
  );
};

export default RubricMappingModal;
